import { useCallback, useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { browserTabCompanySwitchErrorMessage, switchBrowserTabCompany } from '../services/browserTabCompanySwitch'
import { trackBoxTruckUiInteraction } from '../utils/analytics'
import {
    BOX_TRUCK_BROWSER_TAB_PARAM,
    BOX_TRUCK_FRESH_BROWSER_TAB_SENTINEL,
    BoxTruckBrowserTabWorkspace,
    createBoxTruckBrowserTabHandoff,
    readBoxTruckBrowserTabHandoff,
    removeBoxTruckBrowserTabHandoff,
    scheduleBoxTruckBrowserTabHandoffCleanup,
} from '../utils/browserTabHandoff'

const ISOLATED_WINDOW_KEY = 'box_truck_isolated_window'
const HANDOFF_AUTH_WAIT_MS = 8_000
const SESSION_LOADING_ERROR = 'Your session is still loading. Please try again.'
const STORAGE_ERROR = 'Couldn’t prepare this search. Check browser storage permissions and try again.'
const OPEN_ERROR = 'Couldn’t open a new browser tab. Check browser popup permissions and try again.'

type BrowserTabWorkspacePayload = Omit<BoxTruckBrowserTabWorkspace, 'sourceUserId' | 'sourceApplicationId'>
type OpenError = { kind: 'session'; message: typeof SESSION_LOADING_ERROR } | { kind: 'persistent'; message: string } | null

interface BrowserTabHandoffIdentity {
    userId?: string
    applicationId?: string
}

const wasIsolatedWindow = (): boolean => {
    try {
        return sessionStorage.getItem(ISOLATED_WINDOW_KEY) === '1'
    } catch (error) {
        console.warn('[box-truck] Could not read browser-tab isolation state.', error instanceof Error ? error.message : 'Unknown storage error')
        return false
    }
}

/** Source-window action: write a bounded one-time handoff and open its isolated target synchronously. */
export const useOpenBoxTruckBrowserTab = ({ userId, applicationId }: BrowserTabHandoffIdentity) => {
    const [openError, setOpenError] = useState<OpenError>(null)
    const error = openError?.kind === 'session' && userId && applicationId ? null : (openError?.message ?? null)

    const openBrowserTab = useCallback(
        (workspace: BrowserTabWorkspacePayload) => {
            if (!userId || !applicationId) {
                setOpenError({ kind: 'session', message: SESSION_LOADING_ERROR })
                return
            }

            const token = createBoxTruckBrowserTabHandoff({ ...workspace, sourceUserId: userId, sourceApplicationId: applicationId })
            if (!token) {
                setOpenError({ kind: 'persistent', message: STORAGE_ERROR })
                return
            }

            const url = new URL('/dispatcher/box-truck-loadboard', window.location.origin)
            url.searchParams.set(BOX_TRUCK_BROWSER_TAB_PARAM, token)
            try {
                // `noopener`/`noreferrer` intentionally make a successful open return null by specification,
                // so the return value cannot distinguish success from popup blocking. A synchronous throw is
                // still a definite failure; successful restoration is recorded by the target hook below.
                window.open(url.toString(), '_blank', 'noopener,noreferrer')
            } catch (openFailure) {
                // Do not retain selected-driver data when the browser rejects the open operation synchronously.
                removeBoxTruckBrowserTabHandoff(token)
                console.error(
                    '[box-truck] Opening the detached browser tab failed.',
                    openFailure instanceof Error ? `${openFailure.name}: ${openFailure.message}` : 'Unknown browser error'
                )
                setOpenError({ kind: 'persistent', message: OPEN_ERROR })
                return
            }

            setOpenError(null)
            scheduleBoxTruckBrowserTabHandoffCleanup(token)
        },
        [applicationId, userId]
    )

    const dismissError = useCallback(() => setOpenError(null), [])

    return { error, openBrowserTab, dismissError }
}

/** Target-window lifecycle: validate identity, switch company if needed, consume the handoff, and retain isolation. */
export const useBoxTruckBrowserTabHandoff = ({ userId, applicationId }: BrowserTabHandoffIdentity) => {
    const [searchParams, setSearchParams] = useSearchParams()
    // Presence, not truthiness, is security-relevant: `?openSearch=` is malformed but must still open an
    // isolated fallback instead of cloning another window's shared search state.
    const [browserHandoffToken] = useState<string | null>(() =>
        searchParams.has(BOX_TRUCK_BROWSER_TAB_PARAM) ? (searchParams.get(BOX_TRUCK_BROWSER_TAB_PARAM) ?? '') : null
    )
    const requested = browserHandoffToken !== null
    const freshRequested = browserHandoffToken === BOX_TRUCK_FRESH_BROWSER_TAB_SENTINEL
    const [browserHandoff] = useState(() =>
        browserHandoffToken === null || browserHandoffToken === BOX_TRUCK_FRESH_BROWSER_TAB_SENTINEL ? null : readBoxTruckBrowserTabHandoff(browserHandoffToken)
    )
    const handoffWorkspace = browserHandoff?.status === 'valid' ? browserHandoff.workspace : null
    const userMatches = !!handoffWorkspace && !!userId && handoffWorkspace.sourceUserId === userId
    const applicationMatches = !!handoffWorkspace && !!applicationId && handoffWorkspace.sourceApplicationId === applicationId
    const ready = !requested || !handoffWorkspace || (!!userId && !userMatches) || (userMatches && applicationMatches)
    // Fail closed until the authenticated user is known. Page state initializers must never receive another
    // account's driver/search data during a hypothetical same-mount auth transition.
    const importableWorkspace = handoffWorkspace && userMatches ? handoffWorkspace : null
    const [isolatedWindow] = useState(() => requested || wasIsolatedWindow())
    const [isolationMarkerStored, setIsolationMarkerStored] = useState(!isolatedWindow)
    const [switchError, setSwitchError] = useState<string | null>(null)
    const [switching, setSwitching] = useState(false)
    const [switchAttempt, setSwitchAttempt] = useState(0)
    const [noticeDismissed, setNoticeDismissed] = useState(false)
    const successTrackedRef = useRef(false)

    useEffect(() => {
        if (!isolatedWindow) return
        try {
            sessionStorage.setItem(ISOLATED_WINDOW_KEY, '1')
            setIsolationMarkerStored(true)
        } catch (storageError) {
            console.warn(
                '[box-truck] Could not persist browser-tab isolation state.',
                storageError instanceof Error ? storageError.message : 'Unknown storage error'
            )
        }
    }, [isolatedWindow])

    useEffect(() => {
        if (!handoffWorkspace || !userId || !applicationId || !userMatches || applicationMatches) return
        const controller = new AbortController()
        setSwitching(true)
        setSwitchError(null)
        switchBrowserTabCompany(handoffWorkspace.sourceApplicationId, controller.signal)
            .then(() => window.location.replace(window.location.href))
            .catch((companySwitchError: unknown) => {
                if (controller.signal.aborted) return
                const message = browserTabCompanySwitchErrorMessage(companySwitchError)
                console.error('[box-truck] Browser-tab company switch failed.', {
                    message: companySwitchError instanceof Error ? companySwitchError.message : message,
                    targetApplicationId: handoffWorkspace.sourceApplicationId,
                })
                setSwitchError(message)
                setSwitching(false)
            })
        return () => controller.abort()
    }, [applicationId, applicationMatches, handoffWorkspace, switchAttempt, userId, userMatches])

    const retrySwitch = useCallback(() => {
        if (!userId || !applicationId) {
            window.location.reload()
            return
        }
        setSwitchError(null)
        setSwitching(false)
        setSwitchAttempt((attempt) => attempt + 1)
    }, [applicationId, userId])

    useEffect(() => {
        if (!requested || !handoffWorkspace || (userId && applicationId)) return
        const timeout = window.setTimeout(() => {
            setSwitching(false)
            setSwitchError('Your account or company could not be loaded. Try again or open a fresh search.')
        }, HANDOFF_AUTH_WAIT_MS)
        return () => window.clearTimeout(timeout)
    }, [applicationId, handoffWorkspace, requested, switchAttempt, userId])

    const openFresh = useCallback(() => {
        if (browserHandoffToken !== null) removeBoxTruckBrowserTabHandoff(browserHandoffToken)
        window.location.replace(`/dispatcher/box-truck-loadboard?${BOX_TRUCK_BROWSER_TAB_PARAM}=${BOX_TRUCK_FRESH_BROWSER_TAB_SENTINEL}`)
    }, [browserHandoffToken])

    // Count only a target that actually validated and restored the workspace. The source cannot use
    // window.open()'s return value because `noopener` requires a null return even for a successful open.
    useEffect(() => {
        if (successTrackedRef.current || !handoffWorkspace || !userMatches || !applicationMatches) return
        successTrackedRef.current = true
        trackBoxTruckUiInteraction('browser_tab_opened')
    }, [applicationMatches, handoffWorkspace, userMatches])

    useEffect(() => {
        if (browserHandoffToken === null) return
        const waitingForAuth = !!handoffWorkspace && (!userId || !applicationId)
        const waitingForCompany = !!handoffWorkspace && userMatches && !applicationMatches
        if (waitingForAuth || waitingForCompany) return

        removeBoxTruckBrowserTabHandoff(browserHandoffToken)
        // If sessionStorage is blocked, keep the now-invalid handoff parameter in the URL. Its presence is the
        // only reload-safe isolation signal left; removing it would let a reload clone shared window state.
        if (isolatedWindow && !isolationMarkerStored) return
        if (!searchParams.has(BOX_TRUCK_BROWSER_TAB_PARAM)) return
        const next = new URLSearchParams(searchParams)
        next.delete(BOX_TRUCK_BROWSER_TAB_PARAM)
        setSearchParams(next, { replace: true })
    }, [
        applicationId,
        applicationMatches,
        browserHandoffToken,
        handoffWorkspace,
        isolatedWindow,
        isolationMarkerStored,
        searchParams,
        setSearchParams,
        userId,
        userMatches,
    ])

    const waitingForAuth = !!handoffWorkspace && (!userId || !applicationId)
    const needsCompanySwitch = !!handoffWorkspace && userMatches && !applicationMatches
    const notice = noticeDismissed
        ? null
        : requested && !freshRequested && browserHandoff?.status !== 'valid'
          ? browserHandoff?.status === 'expired'
              ? 'This browser-tab link expired. A fresh search was opened instead.'
              : 'This browser-tab link is unavailable. A fresh search was opened instead.'
          : handoffWorkspace && !!userId && !userMatches
            ? 'This search belongs to a different signed-in account. A fresh search was opened instead.'
            : null

    const dismissNotice = useCallback(() => setNoticeDismissed(true), [])

    return {
        handoff: { requested, isolatedWindow, importableWorkspace, ready, notice },
        switchGate: { waitingForAuth, needsCompanySwitch, switching, error: switchError },
        retrySwitch,
        openFresh,
        dismissNotice,
    }
}
